import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaClipboardCheck,
  FaClipboardList,
  FaHome,
  FaHourglassHalf,
  FaSearch,
  FaSyncAlt,
  FaTimesCircle,
  FaClock,
  FaFingerprint,
  FaKeyboard,
  FaMobileAlt,
  FaPlug,
  FaSignInAlt,
  FaSignOutAlt,
  FaUserClock,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import AttendancePieChart from "../ChildComponent/AttendancePieChart ";
import CalendarView from "../ChildComponent/CalendarView";
import MdlRecordStaffAttendance from "../dialog/MdlRecordStaffAttendance";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Created By Cursor
function startOfDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Created By Cursor
function formatDateParts(date) {
  const d = startOfDay(date);
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const mm = month > 9 ? String(month) : `0${month}`;
  const dd = day > 9 ? String(day) : `0${day}`;
  return {
    date: d,
    iso: `${year}-${mm}-${dd}`,
    display: `${dd}-${mm}-${year}`,
  };
}

// Created By Cursor
function shiftDays(date, amount) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + amount);
  return d;
}

// Created By Cursor
function staffInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

// Created By Cursor
function normalizeMark(status) {
  const value = String(status || "").trim();
  if (!value || value === "Not Taken") return "";
  return value;
}

function displayMark(status) {
  const value = normalizeMark(status);
  if (value === "P" || value === "Present") return "Present";
  if (value === "A" || value === "Absent") return "Absent";
  if (value === "L" || value === "Leave") return "Leave";
  if (value === "HD" || value === "Half Day") return "Half Day";
  if (value === "LA" || value === "Late Attendance") return "Late Attendance";
  return value;
}

function markTone(status) {
  const value = normalizeMark(status);
  if (value === "P" || value === "Present") return "p";
  if (value === "A" || value === "Absent") return "a";
  if (value === "L" || value === "Leave") return "l";
  if (value === "HD" || value === "Half Day") return "hd";
  if (value === "LA" || value === "Late Attendance") return "la";
  return "pending";
}

function markCode(status) {
  const value = normalizeMark(status);
  if (value === "P" || value === "Present") return "P";
  if (value === "A" || value === "Absent") return "A";
  if (value === "L" || value === "Leave") return "L";
  if (value === "HD" || value === "Half Day") return "HD";
  if (value === "LA" || value === "Late Attendance") return "LA";
  return "";
}

function staffField(staff, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const value = String(staff?.[keys[i]] || "").trim();
    if (value) return value;
  }
  return "";
}

function formatChipDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const iso = raw.slice(0, 10);
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return raw;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${Number(match[3])} ${months[Number(match[2]) - 1]} ${match[1]}`;
}

function formatChipTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const timePart = raw.includes(" ") ? raw.split(" ").pop() : raw;
  const pieces = String(timePart).split(":");
  const hour = Number(pieces[0]);
  const minute = pieces[1] || "00";
  if (!Number.isFinite(hour)) return String(timePart).slice(0, 5);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
}

function displayPunchType(value) {
  const punch = String(value || "").trim().toUpperCase();
  if (punch === "IN") return "In";
  if (punch === "OUT") return "Out";
  return punch;
}

function displaySource(value) {
  const source = String(value || "").trim().toUpperCase();
  if (source === "MANUAL") return "Manual";
  if (source === "BIOMETRIC") return "Biometric";
  if (source === "MOBILE") return "Mobile";
  if (source === "API") return "API";
  return source;
}

function staffDetailPath(staff, monthYm) {
  const path = `/AdminMain/StaffAttendanceDetail/${encodeURIComponent(
    staff?.staff_id || ""
  )}/${encodeURIComponent(staff?.staff_name || "Staff")}`;
  return monthYm ? `${path}?month=${encodeURIComponent(monthYm)}` : path;
}

function sourceIcon(value) {
  const source = String(value || "").trim().toUpperCase();
  if (source === "BIOMETRIC") return FaFingerprint;
  if (source === "MOBILE") return FaMobileAlt;
  if (source === "API") return FaPlug;
  return FaKeyboard;
}

function isLateMark(status) {
  const value = normalizeMark(status);
  return value === "LA" || value === "Late Attendance";
}

function StaffAttendanceDash() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [monthData, setMonthData] = useState([]);
  const [dateMode, setDateMode] = useState("today");
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [showCalendar, setShowCalendar] = useState(false);
  const [marks, setMarks] = useState({});
  const [recordingStaff, setRecordingStaff] = useState(null);
  const [staffQuery, setStaffQuery] = useState("");

  const dateParts = useMemo(
    () => formatDateParts(selectedDate),
    [selectedDate]
  );
  const month = selectedDate.getMonth() + 1;
  const year = selectedDate.getFullYear();

  const dateModeLabel =
    dateMode === "today"
      ? "Today"
      : dateMode === "yesterday"
        ? "Yesterday"
        : "Custom";

  const sessionBounds = useMemo(() => {
    const session = SessionManager.getCurrentSession()?.[0] || {};
    return {
      start: session.START_DATE || session.start_date || "",
      end: session.END_DATE || session.end_date || "",
    };
  }, []);

  const selectedDay = useMemo(
    () => monthData.find((d) => d.attendance_date === dateParts.iso) || null,
    [monthData, dateParts.iso]
  );

  const isDayEditable = !!selectedDay;

  const applyDayMarks = useCallback((dayRow) => {
    const initial = {};
    (dayRow?.staff || []).forEach((s) => {
      const status = normalizeMark(s.attendance_status);
      if (status) initial[s.staff_id] = status;
    });
    setMarks(initial);
  }, []);

  const loadMonth = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setMonthData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getStaffAttendanceOfMonthYear.php`, {
        params: { school_code: schoolCode, month, year },
      });
      if (res.data?.api_status === "OK") {
        const rows = Array.isArray(res.data.data) ? res.data.data : [];
        setMonthData(rows);
      } else {
        setMonthData([]);
        myToast.notify_warning(res.data?.api_remark || "Could not load attendance.");
      }
    } catch (e) {
      setMonthData([]);
      myToast.notify_warning(
        e?.response?.data?.api_remark || e?.message || "Network error loading attendance."
      );
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    if (!monthData.length) {
      setMarks({});
      return;
    }
    const pick =
      monthData.find((r) => r.attendance_date === dateParts.iso) || monthData[0];
    applyDayMarks(pick);
    setRecordingStaff(null);
    setStaffQuery("");
  }, [monthData, dateParts.iso, applyDayMarks]);

  const stats = useMemo(() => {
    const staff = selectedDay?.staff || [];
    const totalStaff = staff.length;
    let present = 0;
    let absent = 0;
    let leave = 0;
    let halfDay = 0;
    let late = 0;
    staff.forEach((s) => {
      const mark = marks[s.staff_id] || normalizeMark(s.attendance_status);
      if (mark === "Present" || mark === "P") present += 1;
      else if (mark === "Absent" || mark === "A") absent += 1;
      else if (mark === "Leave" || mark === "L") leave += 1;
      else if (mark === "Half Day" || mark === "HD") halfDay += 1;
      else if (isLateMark(mark)) late += 1;
    });
    const marked = present + absent + leave + halfDay + late;
    const pending = Math.max(0, totalStaff - marked);
    const taken = selectedDay?.status === "Taken" || marked > 0;
    const offDay = selectedDay?.status === "Holiday";
    const pct = (n) =>
      totalStaff === 0 ? 0 : Math.floor((n * 100) / totalStaff);
    return {
      totalStaff,
      present,
      absent,
      leave,
      halfDay,
      late,
      marked,
      pending,
      taken,
      offDay,
      dayStatus: selectedDay?.status || "Not Taken",
      presentPct: pct(present),
      absentPct: pct(absent),
      leavePct: pct(leave),
      halfDayPct: pct(halfDay),
      latePct: pct(late),
      pendingPct: pct(pending),
    };
  }, [selectedDay, marks]);

  const groupedStaff = useMemo(() => {
    const staff = selectedDay?.staff || [];
    const q = staffQuery.trim().toLowerCase();
    const recorded = [];
    const notRecorded = [];
    staff.forEach((s) => {
      if (q) {
        const hay = `${s.staff_name || ""} ${s.staff_id || ""} ${s.staff_role || ""} ${s.staff_gender || ""} ${s.staff_email || ""}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      const mark = normalizeMark(s.attendance_status);
      if (mark) recorded.push(s);
      else notRecorded.push(s);
    });
    return { recorded, notRecorded, query: q };
  }, [selectedDay, staffQuery]);

  const selectToday = () => {
    setDateMode("today");
    setSelectedDate(startOfDay(new Date()));
    setShowCalendar(false);
  };

  const selectYesterday = () => {
    setDateMode("yesterday");
    setSelectedDate(shiftDays(new Date(), -1));
    setShowCalendar(false);
  };

  const openCustomCalendar = () => {
    setShowCalendar(true);
  };

  const onCustomDatePick = (date) => {
    const picked = startOfDay(date);
    if (picked > startOfDay(new Date())) {
      myToast.notify_warning("Future dates are not allowed.");
      return;
    }
    setDateMode("custom");
    setSelectedDate(picked);
    setShowCalendar(false);
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const pieReady = !loading && stats.taken && !stats.offDay;

  const renderStaffRow = (s, editable) => {
    const savedMark = normalizeMark(s.attendance_status);
    const tone = markTone(savedMark);
    const role = staffField(s, "staff_role");
    const gender = staffField(s, "staff_gender");
    const punchType = displayPunchType(staffField(s, "punch_type"));
    const punchCode = String(staffField(s, "punch_type")).toUpperCase();
    const sourceCode = String(staffField(s, "source")).toUpperCase();
    const sourceLabel = displaySource(staffField(s, "source"));
    const SourceIcon = sourceIcon(sourceCode);
    const recordedDate =
      formatChipDate(staffField(s, "attendance_date")) ||
      formatChipDate(selectedDay?.attendance_date || dateParts.iso);
    const recordedTime =
      formatChipTime(staffField(s, "attendance_time")) ||
      formatChipTime(staffField(s, "attendance_datetime"));
    const detailTo = staffDetailPath(s, dateParts.iso.slice(0, 7));
    const openDetail = (event) => {
      if (event.target.closest("button")) return;
      history.push(detailTo);
    };
    return (
      <div
        key={`${s.staff_id}-${punchCode || ""}-${staffField(s, "attendance_time")}`}
        className={`sm-att-staff-card sm-att-staff-card--link${savedMark ? ` is-${tone}` : " is-pending"}`}
        role="link"
        tabIndex={0}
        onClick={openDetail}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          if (event.target.closest("button")) return;
          event.preventDefault();
          history.push(detailTo);
        }}
      >
        <div className="sm-att-staff-card__head">
          <div className={`sm-att-staff-row__photo-wrap is-${tone}`} aria-hidden>
            {s.staff_image_path ? (
              <img
                className="sm-att-staff-row__photo"
                src={s.staff_image_path}
                alt=""
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                  const fallback = event.currentTarget.nextElementSibling;
                  if (fallback) fallback.style.display = "inline-flex";
                }}
              />
            ) : null}
            <span
              className="sm-att-staff-row__avatar"
              style={s.staff_image_path ? { display: "none" } : undefined}
            >
              {staffInitials(s.staff_name)}
            </span>
          </div>
          <div className="sm-att-staff-row__body">
            <strong>{s.staff_name}</strong>
            <div className="sm-att-staff-row__meta">
              {s.staff_id ? (
                <span className="sm-att-staff-row__id">{s.staff_id}</span>
              ) : null}
              {gender ? <span className="sm-att-staff-row__chip">{gender}</span> : null}
              {role ? <span>{role}</span> : null}
            </div>
          </div>
          {savedMark ? (
            <span className={`sm-att-staff-row__badge is-${tone}`}>
              <em>{markCode(savedMark)}</em>
              {displayMark(savedMark)}
            </span>
          ) : editable ? (
            <button
              type="button"
              className="sm-att-record-btn"
              onClick={() => setRecordingStaff(s)}
            >
              <FaClipboardCheck aria-hidden /> Record
            </button>
          ) : (
            <span className="sm-att-staff-row__badge is-pending">Not recorded</span>
          )}
          <span className="sm-att-staff-row__go" aria-hidden>
            <FaChevronRight />
          </span>
        </div>
        {savedMark ? (
          <div className="sm-att-staff-row__facts">
            {punchType ? (
              <span
                className={`sm-att-fact is-punch-${punchCode === "OUT" ? "out" : "in"}`}
                title="Punch type"
              >
                {punchCode === "OUT" ? (
                  <FaSignOutAlt aria-hidden />
                ) : (
                  <FaSignInAlt aria-hidden />
                )}
                {punchType}
              </span>
            ) : null}
            {sourceLabel ? (
              <span
                className={`sm-att-fact is-source-${sourceCode.toLowerCase() || "manual"}`}
                title="Source"
              >
                <SourceIcon aria-hidden />
                {sourceLabel}
              </span>
            ) : null}
            {recordedDate ? (
              <span className="sm-att-fact is-date" title="Attendance date">
                <FaCalendarAlt aria-hidden />
                {recordedDate}
              </span>
            ) : null}
            {recordedTime ? (
              <span className="sm-att-fact is-time" title="Attendance time">
                <FaClock aria-hidden />
                {recordedTime}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="content payroll-page sm-page atd-page"
      style={{ fontFamily: PAGE_FONT, background: "#ffffff" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <Link to="/AdminMain/StaffManagement" style={{ color: COLORS.white }}>
                Staff Management
              </Link>
            </li>
            <li>
              <Link to="/AdminMain/StaffAttendanceDash" style={{ color: COLORS.white }}>
                Staff Attendance
              </Link>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell atd-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardCheck />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Staff Attendance</h1>
              <p className="sm-hero__sub">
                {dateModeLabel} · {dateParts.display} · {stats.marked} marked ·{" "}
                {stats.pending} pending
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadMonth}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="atd-nav-actions" aria-label="Attendance views">
          <Link to="/AdminMain/StaffMonthAttendance" className="atd-nav-btn">
            <span className="atd-nav-btn__icon" aria-hidden>
              <FaCalendarAlt />
            </span>
            <span className="atd-nav-btn__text">
              <strong>Monthly View Attendance</strong>
              <small>Staff calendar · take or review by day</small>
            </span>
          </Link>
          <Link to="/AdminMain/StaffAttendance" className="atd-nav-btn">
            <span className="atd-nav-btn__icon is-list" aria-hidden>
              <FaClipboardList />
            </span>
            <span className="atd-nav-btn__text">
              <strong>Attendance Staff</strong>
              <small>Staff list · current month stats</small>
            </span>
          </Link>
        </div>

        <div className="atd-body">
          <section className="mat-report-card atd-report">
            <div className="mat-report-card__head atd-report-head">
              <div className="mat-report-card__title">
                <span className="mat-report-card__icon" aria-hidden>
                  <FaClipboardCheck />
                </span>
                <div className="min-w-0">
                  <h2>Attendance report</h2>
                  <div className="atd-report-subrow">
                    <p>
                      {dateModeLabel} · {dateParts.display}
                      {loading
                        ? " · Loading…"
                        : stats.offDay
                          ? ` · ${stats.dayStatus}`
                          : stats.taken
                            ? ` · ${stats.marked} of ${stats.totalStaff} marked`
                            : " · No attendance taken yet"}
                    </p>
                    <div
                      className="atd-date-seg"
                      role="tablist"
                      aria-label="Report date"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dateMode === "today"}
                        className={`atd-date-seg__btn${
                          dateMode === "today" ? " is-active" : ""
                        }`}
                        onClick={selectToday}
                        disabled={loading}
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dateMode === "yesterday"}
                        className={`atd-date-seg__btn${
                          dateMode === "yesterday" ? " is-active" : ""
                        }`}
                        onClick={selectYesterday}
                        disabled={loading}
                      >
                        Yesterday
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={dateMode === "custom" || showCalendar}
                        className={`atd-date-seg__btn${
                          dateMode === "custom" || showCalendar
                            ? " is-active"
                            : ""
                        }`}
                        onClick={openCustomCalendar}
                      >
                        <FaCalendarAlt aria-hidden /> Custom
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <span
                className={`mat-report-badge${
                  !loading && stats.taken && !stats.offDay
                    ? ""
                    : " mat-report-badge--pending"
                }`}
              >
                {loading
                  ? "…"
                  : stats.offDay
                    ? stats.dayStatus
                    : stats.taken
                      ? dateModeLabel
                      : "Pending"}
              </span>
            </div>

            {loading ? (
              <div className="atd-loading atd-loading--report">
                <MyLoader />
              </div>
            ) : (
              <>
                <div className="mat-report-body">
                  <div className="mat-report-chart">
                    <AttendancePieChart
                      Present={pieReady ? stats.present : "N/A"}
                      Absent={pieReady ? stats.absent : "N/A"}
                      Leave={pieReady ? stats.leave : "N/A"}
                      HalfDay={pieReady ? stats.halfDay : "N/A"}
                      Late={pieReady ? stats.late : "N/A"}
                      size={180}
                      showLegend
                    />
                  </div>

                  <div className="mat-report-breakdown">
                    <div className="mat-report-bar is-p">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaCheckCircle aria-hidden /> Present
                        </span>
                        <strong>
                          {stats.present}
                          <small>{stats.presentPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i style={{ width: `${Math.min(100, stats.presentPct)}%` }} />
                      </div>
                    </div>

                    <div className="mat-report-bar is-a">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaTimesCircle aria-hidden /> Absent
                        </span>
                        <strong>
                          {stats.absent}
                          <small>{stats.absentPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i style={{ width: `${Math.min(100, stats.absentPct)}%` }} />
                      </div>
                    </div>

                    <div className="mat-report-bar is-l">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaCalendarAlt aria-hidden /> Leave
                        </span>
                        <strong>
                          {stats.leave}
                          <small>{stats.leavePct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i style={{ width: `${Math.min(100, stats.leavePct)}%` }} />
                      </div>
                    </div>

                    <div className="mat-report-bar is-hd">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaUserClock aria-hidden /> Half Day
                        </span>
                        <strong>
                          {stats.halfDay}
                          <small>{stats.halfDayPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i style={{ width: `${Math.min(100, stats.halfDayPct)}%` }} />
                      </div>
                    </div>

                    <div className="mat-report-bar is-la">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaClock aria-hidden /> Late Attendance
                        </span>
                        <strong>
                          {stats.late}
                          <small>{stats.latePct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i style={{ width: `${Math.min(100, stats.latePct)}%` }} />
                      </div>
                    </div>

                    <div className="mat-report-bar is-pending">
                      <div className="mat-report-bar__meta">
                        <span>
                          <FaHourglassHalf aria-hidden /> Pending
                        </span>
                        <strong>
                          {stats.pending}
                          <small>{stats.pendingPct}%</small>
                        </strong>
                      </div>
                      <div className="mat-report-bar__track" aria-hidden>
                        <i style={{ width: `${Math.min(100, stats.pendingPct)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mat-report-foot">
                  <div className="mat-report-chip">
                    <span className="mat-report-chip__icon is-sky" aria-hidden>
                      <FaUsers />
                    </span>
                    <div>
                      <span>Total staff</span>
                      <strong>{stats.totalStaff}</strong>
                    </div>
                  </div>
                  <div className="mat-report-chip">
                    <span className="mat-report-chip__icon is-mint" aria-hidden>
                      <FaCheckCircle />
                    </span>
                    <div>
                      <span>Marked</span>
                      <strong>{stats.marked}</strong>
                    </div>
                  </div>
                  <div
                    className={`mat-report-chip${
                      stats.pending > 0 && !stats.offDay ? " is-warn" : ""
                    }`}
                  >
                    <span
                      className={`mat-report-chip__icon${
                        stats.pending > 0 && !stats.offDay ? " is-warn" : " is-sky"
                      }`}
                      aria-hidden
                    >
                      {stats.pending > 0 && !stats.offDay ? (
                        <FaHourglassHalf />
                      ) : (
                        <FaUserClock />
                      )}
                    </span>
                    <div>
                      <span>{stats.offDay ? stats.dayStatus : "Pending"}</span>
                      <strong>{stats.offDay ? "—" : stats.pending}</strong>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          {!loading ? (
            <>
              <section className="atd-section atd-section--last">
                <div className="atd-section__head sm-section-head">
                  <div className="min-w-0">
                    <h2 className="atd-section__title">
                      {dateMode === "custom"
                        ? "Custom overview"
                        : `${dateModeLabel}’s overview`}
                    </h2>
                    <p className="atd-section__hint">
                      Record Present, Absent, Half Day, Leave or Late Attendance · {dateParts.display}
                    </p>
                  </div>
                  <label className="sm-att-staff-search">
                    <FaSearch aria-hidden />
                    <input
                      type="search"
                      placeholder="Search staff"
                      value={staffQuery}
                      onChange={(e) => setStaffQuery(e.target.value)}
                      aria-label="Search staff"
                    />
                  </label>
                </div>

                {!selectedDay ? (
                  <div className="atd-empty">No staff found for this date.</div>
                ) : (
                  <div className="sm-att-staff-split">
                    <article className="sm-att-staff-panel sm-att-staff-panel--recorded">
                      <header className="sm-att-staff-panel__head">
                        <div className="min-w-0">
                          <h3>Attendance recorded</h3>
                          <p>
                            {groupedStaff.recorded.length} staff marked
                          </p>
                        </div>
                        <span className="sm-att-staff-panel__count">
                          {groupedStaff.recorded.length}
                        </span>
                      </header>
                      {groupedStaff.recorded.length === 0 ? (
                        <div className="sm-att-staff-group__empty">
                          {groupedStaff.query
                            ? "No matching recorded staff."
                            : "No attendance recorded yet."}
                        </div>
                      ) : (
                        <div className="sm-att-staff-list sm-att-staff-list--cards">
                          {groupedStaff.recorded.map((s) =>
                            renderStaffRow(s, isDayEditable)
                          )}
                        </div>
                      )}
                    </article>

                    <article className="sm-att-staff-panel sm-att-staff-panel--pending">
                      <header className="sm-att-staff-panel__head">
                        <div className="min-w-0">
                          <h3>Not recorded</h3>
                          <p>
                            {groupedStaff.notRecorded.length} staff pending
                          </p>
                        </div>
                        <span className="sm-att-staff-panel__count">
                          {groupedStaff.notRecorded.length}
                        </span>
                      </header>
                      {groupedStaff.notRecorded.length === 0 ? (
                        <div className="sm-att-staff-group__empty">
                          {groupedStaff.query
                            ? "No matching pending staff."
                            : "All staff attendance is recorded."}
                        </div>
                      ) : (
                        <div className="sm-att-staff-list sm-att-staff-list--cards">
                          {groupedStaff.notRecorded.map((s) =>
                            renderStaffRow(s, true)
                          )}
                        </div>
                      )}
                    </article>
                  </div>
                )}
              </section>
            </>
          ) : null}

          {recordingStaff ? (
            <MdlRecordStaffAttendance
              staff={recordingStaff}
              attendanceDate={dateParts.iso}
              attendanceDateDisplay={dateParts.display}
              actionClose={() => setRecordingStaff(null)}
              actionSaved={() => {
                setRecordingStaff(null);
                loadMonth();
              }}
            />
          ) : null}

          {showCalendar
            ? createPortal(
                <div className="atd-cal-modal" role="dialog" aria-modal="true">
                  <button
                    type="button"
                    className="atd-cal-modal__backdrop"
                    aria-label="Close calendar"
                    onClick={() => setShowCalendar(false)}
                  />
                  <div
                    className="atd-cal-modal__panel"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="atd-cal-modal__hero">
                      <div className="atd-cal-modal__hero-icon" aria-hidden>
                        <FaCalendarAlt />
                      </div>
                      <div className="min-w-0">
                        <h3>Select date</h3>
                        <p>Pick any past date to load staff attendance</p>
                      </div>
                      <button
                        type="button"
                        className="atd-cal-modal__close"
                        onClick={() => setShowCalendar(false)}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="atd-cal-modal__body">
                      <label className="atd-cal-native">
                        <span>Quick pick</span>
                        <input
                          type="date"
                          max={formatDateParts(new Date()).iso}
                          value={dateParts.iso}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (!value) return;
                            onCustomDatePick(new Date(`${value}T00:00:00`));
                          }}
                        />
                      </label>

                      <div className="atd-cal-modal__calendar">
                        <CalendarView
                          events={[]}
                          columns={7}
                          subtitle=""
                          disableFutureDates
                          selectedDate={selectedDate}
                          showLegend={false}
                          onDayPress={onCustomDatePick}
                          onDayLongPress={() => {}}
                          onPrevPress={() => {}}
                          onNextPress={() => {}}
                          start_date={sessionBounds.start || "2000-01-01"}
                          end_date={
                            sessionBounds.end || formatDateParts(new Date()).iso
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>,
                document.body
              )
            : null}
        </div>
      </div>
    </div>
  );
}

export default StaffAttendanceDash;
